import { useState, useCallback, ReactNode } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiChevronDown, FiChevronRight, FiExternalLink, FiDownload, FiBarChart2 } from "react-icons/fi";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { generatePdf } from "components/RFQ/PdfDownloadButton/api-PdfDownload";
import { CompanyLogoDisplay } from "newui/features/Company/components/CompanyLogo/CompanyLogoDisplay";

interface ScoringAccordionItem {
	id: number;
	logo?: string | null;
	name: string;
	score: string | number | null;
}

interface ScoringAccordionProps {
	children: (item: ScoringAccordionItem, index: number) => ReactNode;
	items: ScoringAccordionItem[];
	rfxId: number;
	extraHeaderContent?: (item: ScoringAccordionItem) => ReactNode;
}

export const ScoringAccordion: React.FC<ScoringAccordionProps> = ({
	items,
	rfxId,
	children,
	extraHeaderContent,
}) => {
	const { clientPaths } = useNavigationPaths();
	const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
	const [pdfLoadingId, setPdfLoadingId] = useState<number | null>(null);

	const toggleItem = useCallback((id: number) => {
		setExpandedItems((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const handlePdfDownload = useCallback((inviteId: number) => {
		setPdfLoadingId(inviteId);
		generatePdf("responses", rfxId, inviteId, false).finally(() => {
			setPdfLoadingId(null);
		});
	}, [rfxId]);

	return (
		<div className="space-y-2">
			{items.map((item, index) => {
				const isExpanded = expandedItems.has(item.id);

				return (
					<div
						key={item.id}
						className="rounded-lg border border-gray-200 bg-white"
					>
						{/* Accordion header */}
						<div
							className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors rounded-lg cursor-pointer"
							onClick={() => toggleItem(item.id)}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									toggleItem(item.id);
								}
							}}
							aria-expanded={isExpanded}
						>
							<div className="flex items-center gap-2">
								{isExpanded ? (
									<FiChevronDown size={16} className="shrink-0 text-gray-400" />
								) : (
									<FiChevronRight size={16} className="shrink-0 text-gray-400" />
								)}
								{item.score !== null && item.score !== "—" && (
									<span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-[#e8eeff] text-[#0031DD]">
										<FiBarChart2 size={10} aria-hidden="true" />
										{item.score}
									</span>
								)}
								<CompanyLogoDisplay
									size="sm"
									supplierLogoUrl={item.logo ?? null}
									supplierName={item.name}
								/>
								<span className="text-sm font-medium text-gray-900">
									{item.name}
								</span>
							</div>
							<div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
								{extraHeaderContent?.(item)}
								<a
									href={clientPaths.viewRfxSubmission(rfxId, item.id)}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
								>
									<FiExternalLink size={12} />
									View Response
								</a>
								<button
									type="button"
									onClick={() => handlePdfDownload(item.id)}
									disabled={pdfLoadingId === item.id}
									className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{pdfLoadingId === item.id ? <Spinner size="xs" /> : <FiDownload size={12} />}
									PDF
								</button>
							</div>
						</div>

						{/* Expanded content */}
						{isExpanded && children(item, index)}
					</div>
				);
			})}
		</div>
	);
};
